<template>
  <div class="container">
    <div v-if="loading" class="loading">
      <van-loading size="24px" color="#1989fa" vertical>加载中...</van-loading>
    </div>
    <div v-else-if="error" class="error">
      <van-empty description="无法加载PDF文件" />
      <van-button type="primary" size="small" @click="openInBrowser" style="margin-top: 16px;">
        在浏览器中打开
      </van-button>
    </div>
    <div v-else class="pdf-container">
      <div class="pdf-toolbar">
        <span class="page-info">{{ currentPage }} / {{ totalPages }}</span>
        <van-slider 
          v-model="sliderValue" 
          :min="1" 
          :max="totalPages" 
          @change="onSliderChange" 
          class="page-slider" 
        />
        <div class="zoom-controls">
          <van-button size="mini" icon="minus" @click="zoomOut" :disabled="scale <= 0.5" />
          <span class="zoom-value">{{ Math.round(scale * 100) }}%</span>
          <van-button size="mini" icon="plus" @click="zoomIn" :disabled="scale >= 3" />
        </div>
      </div>
      <div class="pdf-viewer" ref="pdfViewer">
        <!-- 多页渲染，支持连续滚动 -->
        <div class="pdf-pages-container">
          <canvas
            v-for="pageNum in totalPages"
            :key="pageNum"
            :ref="`pdfCanvas-${pageNum}`"
            class="pdf-page"
          ></canvas>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import * as pdfjsLib from "pdfjs-dist";

pdfjsLib.GlobalWorkerOptions.workerSrc = "https://cdn.jsdelivr.net/npm/pdfjs-dist@2.5.207/build/pdf.worker.min.js";

export default {
  name: "PdfPreview",
  data() {
    return {
      loading: true,
      error: false,
      pdfUrl: null,
      pdfDoc: null,
      currentPage: 1,
      totalPages: 0,
      sliderValue: 1,
      scale: 1.0,
      baseScale: 1.0,
    };
  },
  watch: {
    // 监听缩放变化，重新渲染所有页面
    scale() {
      this.reRenderAllPages();
    },
  },
  mounted() {
    const params = this.$route.params;
    const url = params.url;
    const title = params.title;

    if (title) {
      document.title = decodeURIComponent(title);
    }

    if (url) {
      this.pdfUrl = decodeURIComponent(url);
      this.loadPdf();
    } else {
      this.loading = false;
      this.error = true;
    }
  },
  methods: {
    async loadPdf() {
      try {
        this.loading = true;
        this.error = false;

        if (!this.pdfUrl || this.pdfUrl === 'undefined') {
          throw new Error('PDF URL 无效');
        }

        const loadingTask = pdfjsLib.getDocument({
          url: this.pdfUrl,
          cMapUrl: "https://cdn.jsdelivr.net/npm/pdfjs-dist@2.5.207/cmaps/",
          cMapPacked: true,
          withCredentials: false,
        });

        this.pdfDoc = await loadingTask.promise;
        this.totalPages = this.pdfDoc.numPages;
        this.loading = false;

        this.$nextTick(() => {
          this.calculateBaseScale();
          this.renderAllPages();
          this.initScrollListener();
        });
      } catch (err) {
        console.error("PDF加载失败:", err);
        this.loading = false;
        this.error = true;
      }
    },
    calculateBaseScale() {
      const containerWidth = this.$refs.pdfViewer.clientWidth - 24;
      const standardPdfWidth = 595;
      this.baseScale = containerWidth / standardPdfWidth;
    },
    async renderAllPages() {
      for (let pageNum = 1; pageNum <= this.totalPages; pageNum++) {
        await this.renderPage(pageNum);
      }
    },
    async renderPage(pageNum) {
      if (!this.pdfDoc) return;
      
      try {
        const page = await this.pdfDoc.getPage(pageNum);
        const canvas = this.$refs[`pdfCanvas-${pageNum}`];
        if (!canvas || !canvas[0]) return;

        const canvasElement = canvas[0];
        const context = canvasElement.getContext("2d");

        // 使用基础比例 * 用户缩放比例渲染
        const finalScale = this.baseScale * this.scale;
        const viewport = page.getViewport({ scale: finalScale });

        // 设置 canvas 尺寸
        canvasElement.height = viewport.height;
        canvasElement.width = viewport.width;

        // 清空画布
        context.clearRect(0, 0, canvasElement.width, canvasElement.height);

        // 渲染页面
        await page.render({
          canvasContext: context,
          viewport: viewport,
        }).promise;
      } catch (err) {
        console.error(`渲染第${pageNum}页失败:`, err);
      }
    },
    async reRenderAllPages() {
      // 保存当前视图中心点相对于文档的比例位置
      const viewer = this.$refs.pdfViewer;
      const oldScrollTop = viewer.scrollTop;
      const oldScrollLeft = viewer.scrollLeft;
      const oldScrollHeight = viewer.scrollHeight;
      const oldScrollWidth = viewer.scrollWidth;
      
      // 计算中心点比例（0~1）
      const centerRatioY = oldScrollHeight > viewer.clientHeight 
        ? (oldScrollTop + viewer.clientHeight / 2) / oldScrollHeight 
        : 0.5;
      const centerRatioX = oldScrollWidth > viewer.clientWidth 
        ? (oldScrollLeft + viewer.clientWidth / 2) / oldScrollWidth 
        : 0.5;
      
      // 重新渲染所有页面
      await this.renderAllPages();
      
      // 根据新的文档尺寸恢复中心点位置
      this.$nextTick(() => {
        const newScrollHeight = viewer.scrollHeight;
        const newScrollWidth = viewer.scrollWidth;
        
        viewer.scrollTop = centerRatioY * newScrollHeight - viewer.clientHeight / 2;
        viewer.scrollLeft = centerRatioX * newScrollWidth - viewer.clientWidth / 2;
      });
    },
    initScrollListener() {
      const viewer = this.$refs.pdfViewer;
      if (!viewer) return;

      viewer.addEventListener('scroll', () => {
        // 根据滚动位置计算当前页
        const scrollTop = viewer.scrollTop;
        const pageHeight = viewer.scrollHeight / this.totalPages;
        const newPage = Math.floor(scrollTop / pageHeight) + 1;
        if (newPage !== this.currentPage && newPage >= 1 && newPage <= this.totalPages) {
          this.currentPage = newPage;
          this.sliderValue = newPage;
        }
      });
    },
    onSliderChange(value) {
      this.currentPage = value;
      this.sliderValue = value;
      // 滚动到对应页面
      const canvas = this.$refs[`pdfCanvas-${value}`];
      if (canvas && canvas[0]) {
        canvas[0].scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    },
    zoomIn() {
      if (this.scale < 3) {
        this.scale = Math.round((this.scale + 0.25) * 100) / 100;
      }
    },
    zoomOut() {
      if (this.scale > 0.5) {
        this.scale = Math.round((this.scale - 0.25) * 100) / 100;
      }
    },
    openInBrowser() {
      if (this.pdfUrl) {
        window.open(this.pdfUrl, "_blank");
      }
    },
  },
};
</script>

<style lang="less" scoped>
.container {
  height: 100vh;
  background-color: #f5f5f5;
  display: flex;
  flex-direction: column;

  .loading {
    width: 100%;
    height: 100vh;
    display: flex;
    justify-content: center;
    align-items: center;
  }

  .error {
    width: 100%;
    height: 100vh;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
  }

  .pdf-container {
    flex: 1;
    display: flex;
    flex-direction: column;
    overflow: hidden;

    .pdf-toolbar {
      background-color: #fff;
      padding: 8px 16px;
      display: flex;
      align-items: center;
      border-bottom: 1px solid #ebedf0;
      flex-shrink: 0;

      .page-info {
        font-size: 14px;
        color: #666;
        margin-right: 12px;
        white-space: nowrap;
        min-width: 50px;
      }

      .page-slider {
        flex: 1;
        margin-right: 12px;
      }

      .zoom-controls {
        display: flex;
        align-items: center;
        gap: 8px;

        .zoom-value {
          font-size: 13px;
          color: #666;
          min-width: 45px;
          text-align: center;
        }
      }
    }

    .pdf-viewer {
      flex: 1;
      overflow: auto;
      padding: 12px;
      -webkit-overflow-scrolling: touch;
      
      .pdf-pages-container {
        display: flex;
        flex-direction: column;
        align-items: flex-start; // 左对齐，支持横向滚动
        min-width: min-content; // 确保容器宽度适应内容
      }

      .pdf-page {
        display: block;
        margin-bottom: 12px;
        background-color: #fff;
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
        max-width: none; // 移除最大宽度限制
        
        &:last-child {
          margin-bottom: 0;
        }
      }
    }
  }
}
</style>
